import React, { useState } from 'react';

import { VALIDATION_REGEX } from '../../../../../shared/components/validation.config';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { CompanyUiModel } from '../../../../../shared/ui-models/company.ui-model';

export interface AddCompanyModalProps {
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    addCompanyHandler: (company: CompanyUiModel) => void;
  };
}

export const AddCompanyModal: React.FC<AddCompanyModalProps> = ({ config, eventHandlers }) => {
  const [companyName, setCompanyName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [contactPersonName, setContactPersonName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [contactPersonEmail, setContactPersonEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [contactPersonPhone, setContactPersonPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const isMobileView = useWindowResizer();

  const handleCompanySubmit = () => {
    if (
      companyName.isValid &&
      contactPersonName.isValid &&
      contactPersonEmail.isValid &&
      contactPersonPhone.isValid
    ) {
      eventHandlers.addCompanyHandler({
        name: companyName.value,
        contactPersonName: contactPersonName.value,
        contactPersonEmail: contactPersonEmail.value,
        contactPersonPhoneNumber: contactPersonPhone.value
      });
    }
  };

  const isFormValid =
    companyName.isValid &&
    contactPersonName.isValid &&
    contactPersonEmail.isValid &&
    contactPersonPhone.isValid;

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.cancelHandler();
        }
      }}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <div className="text-2xl font-gilroy-bold font-semibold text-neutral-900">Add Company</div>
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">Company name</div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid company name',
            placeholder: 'Enter company name'
          }}
          config={{
            maxRows: 1,
            autoFocus: true,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setCompanyName({ value, isValid });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person name
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid name',
            placeholder: 'Enter contact person name'
          }}
          config={{
            maxRows: 1,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonName({ value, isValid });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person email
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid email',
            placeholder: 'Enter contact person email'
          }}
          config={{
            maxRows: 1,
            type: InputTextType.email,
            validationRegex: VALIDATION_REGEX.EMAIL,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonEmail({ value, isValid });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person phone
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a phone number',
            placeholder: 'Enter contact person phone'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonPhone({ value, isValid });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
      </div>
      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button
          onClick={() => {
            eventHandlers.cancelHandler();
            trackEvent('cancel', {
              category: 'company',
              label: 'add-company-modal'
            });
          }}
          tracking={{
            action: 'cancel',
            category: 'company',
            label: 'add-company-modal'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          Cancel
        </Button>
        <Button
          disabled={!isFormValid}
          onClick={handleCompanySubmit}
          tracking={{
            action: 'submit',
            category: 'company',
            label: 'add-company-modal'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Add Company
        </Button>
      </div>
    </Modal>
  );
};
